import { useContext } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ApiContext } from '@shared/contexts';
import type { TSearchQuery, TSearchResponse } from './types';
import { SEARCH_ENDPOINT, SEARCH_QUERY_KEY, MIN_SEARCH_QUERY_LENGTH } from './constants';
import { useDebounce } from 'use-debounce';

const defaultOptions = {
  isDisabled: false,
};

type TUseSearchParams = Omit<TSearchQuery, 'query' | 'page'> & {
  query: TSearchQuery['query'] | null;
  page?: TSearchQuery['page'];
};

// --- GET
export const useSearch = (
  params: TUseSearchParams,
  options: {
    isDisabled?: boolean;
  } = defaultOptions
) => {
  const { query, pageSize, page = 1, folderId, itemTypes, blocksCondition, templatesCondition } = params;
  const { apiClient, useHandleQueryError, retry } = useContext(ApiContext);

  const [debouncedQuery] = useDebounce(query?.trim(), 300);
  const itemTypesParam = itemTypes?.join(',');

  const queryFn = async (): Promise<TSearchResponse | undefined> => {
    if (!debouncedQuery || !page || !pageSize) {
      // should not happen, but just in case
      console.warn('Search query, page, or pageSize is not provided. Returning empty search response.');
      return;
    }
    const { data } = await apiClient.get<TSearchResponse>(SEARCH_ENDPOINT, {
      // API expects 0-based page index, so we subtract 1 from the provided page
      params: {
        query: debouncedQuery,
        page: page - 1,
        pageSize,
        ...(folderId ? { folderId } : {}),
        ...(itemTypesParam ? { itemTypes: itemTypesParam } : {}),
        ...(blocksCondition ? { blocksCondition } : {}),
        ...(templatesCondition ? { templatesCondition } : {}),
      },
    });
    return data;
  };

  const { data, isError, error, isLoading } = useQuery({
    queryKey: [
      SEARCH_QUERY_KEY,
      debouncedQuery,
      page,
      pageSize,
      folderId,
      itemTypesParam,
      blocksCondition,
      templatesCondition,
    ],
    /**
     * Enable query if:
     * - query is provided and has at least MIN_SEARCH_QUERY_LENGTH characters
     * - page is provided and is a valid number
     * - pageSize is provided and is a valid number
     */
    enabled:
      !!debouncedQuery &&
      debouncedQuery.length >= MIN_SEARCH_QUERY_LENGTH &&
      !!page &&
      !!pageSize &&
      !options.isDisabled,
    queryFn,
    retry,
    placeholderData: keepPreviousData,
  });

  useHandleQueryError(isError, error, `Failed to load search results for "${debouncedQuery}" on page ${page}!`);

  return { data, isLoading, error };
};
